import type { ServiceItem, ServiceTransaction } from '../../types';
import { buchungsartVon, isBackfilledAltEntry, isManualPosting, isPostingEntry, istBelegBuchung, reportNumberOf } from '../../services/serviceLogUtils';
import { openQuantity, waivedQuantity } from '../../services/projectLv';
import { contractedQuantity, isNepService } from '../../services/nepPosition';

/**
 * Anzeige-Ableitung fuer die Detailansicht EINER Position im Dialog
 * „Leistungen Details" (ServiceDetailsModal → ServiceDetailView).
 *
 * Reine Lesesicht: hier wird nichts gerechnet, was nicht schon anderswo die
 * Wahrheit ist. Offen/verzichtet kommen aus projectLv, beauftragt aus
 * nepPosition, die Einstufung Arbeit/Buchung aus serviceLogUtils — dieselben
 * Stellen, an denen Rechnung und Projektcheck haengen. Eine eigene Regel hier
 * waere eine zweite Wahrheit, die still auseinanderlaeuft.
 */

export type ServiceDetailEntryKind = 'work' | 'billing' | 'waiver' | 'reversal';

export interface ServiceDetailEntry {
    id: string;
    kind: ServiceDetailEntryKind;
    /** ISO-Zeitpunkt; bei Abrechnungszeilen der Buchungszeitpunkt, sonst der Leistungstag. */
    date: string;
    quantity: number;
    performerName: string;
    isSubstitution: boolean;
    reportNumber: number | null;
    description: string;
    nepReason?: string;
    reference?: string;
    bookedBy?: string;
}

export interface ServiceDetailSummary {
    contracted: number;
    used: number;
    billed: number;
    waived: number;
    open: number;
    /** Noch nicht erbracht bis zur beauftragten Menge (nie negativ). */
    remaining: number;
}

export interface ServiceDetail {
    summary: ServiceDetailSummary;
    /** Erbrachte Leistung, neueste zuerst. */
    work: ServiceDetailEntry[];
    workTotal: number;
    /** Abrechnung, Verzicht, Ruecknahme — neueste zuerst. */
    postings: ServiceDetailEntry[];
}

/**
 * Arbeit oder Buchung — und wenn Buchung, welche.
 *
 * Die Frage „Buchung?" beantwortet isPostingEntry (Journal-Art, sonst
 * Praefix/Text), „mit Beleg?" istBelegBuchung. Was Buchung, aber kein Beleg
 * ist, ist Verzicht ('VZ-') oder Ruecknahme ('RN-').
 *
 * Die 'ALT-'-Zeile zuerst: mit dem Text „manuelle Verbuchung" nennt der
 * Rueckfall sie Buchung, das Journal `legacy_backfill` und damit Leistung —
 * das Journal hat recht (serviceLogUtils.isPostingEntry). Ohne geladenes
 * Register stuende sie sonst unter den Buchungen.
 */
export const classifyServiceEntry = (entry: ServiceTransaction): ServiceDetailEntryKind => {
    if (isBackfilledAltEntry(entry)) return 'work';
    if (!isPostingEntry(entry)) return 'work';
    if (istBelegBuchung(entry)) return 'billing';
    const art = buchungsartVon(entry);
    if (art === 'waiver' || (entry.bookingReference || '').startsWith('VZ-')) return 'waiver';
    return 'reversal';
};

/** Beschriftung einer Buchungszeile. */
export const postingLabel = (entry: ServiceDetailEntry): string => {
    if (entry.kind === 'waiver') return 'Verzicht';
    if (entry.kind === 'reversal') return 'Rücknahme';
    if (entry.quantity < 0) return 'Storno';
    if ((entry.reference || '').startsWith('MB-') || isManualPosting(entry)) return 'Verbuchung';
    return 'Abrechnung';
};

/**
 * Betraege in Freitext fuer den Aussendienst unkenntlich machen — dieselbe
 * Regel wie bei den Notizen in ProjectStats. Beschreibung und NEP-Begruendung
 * sind frei getippt und koennen Preise enthalten.
 */
export const maskAmounts = (text: string): string =>
    text.replace(/\b\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?\s?(?:€|EUR)/gi, '*** €');

const zeitwert = (iso: string): number => {
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
};

const neuesteZuerst = (a: ServiceDetailEntry, b: ServiceDetailEntry): number => zeitwert(b.date) - zeitwert(a.date);

const toEntry = (log: ServiceTransaction): ServiceDetailEntry => {
    const kind = classifyServiceEntry(log);
    return {
        id: log.id,
        kind,
        date: (kind === 'work' ? log.date : log.bookedAt || log.date) || '',
        quantity: Number(log.quantity) || 0,
        performerName: log.performerName || '',
        isSubstitution: log.isSubstitution === true,
        reportNumber: kind === 'work' ? reportNumberOf(log) : null,
        description: log.description || '',
        nepReason: log.nepReason,
        reference: log.bookingReference,
        bookedBy: log.bookedBy,
    };
};

export const summarizeService = (service: ServiceItem, flatCovered = false): ServiceDetailSummary => {
    const contracted = contractedQuantity(service);
    const used = service.quantityUsed || 0;
    return {
        contracted,
        used,
        billed: service.quantityBilled || 0,
        waived: waivedQuantity(service),
        // Der Dialog bekommt die rohe Liste ohne Deckungsstempel (siehe
        // flatCoverageHints) — die Deckung kommt deshalb als Flag herein.
        open: flatCovered ? 0 : openQuantity(service),
        remaining: Math.max(0, contracted - used),
    };
};

/**
 * Stand einer Position im Buchungsdialog: bisher gebucht, Soll, offen — und
 * wie es nach der eingegebenen Menge aussaehe.
 *
 * „Offen" heisst hier: noch nicht erbracht bis zur beauftragten Menge (nicht
 * „nicht abgerechnet" — das ist Sache der Buchhaltung und haengt an
 * openQuantity). Nur Mengen, keine Preise: der Dialog steht jedem offen.
 *
 * NEP: beauftragt ist, was gebucht ist (contractedQuantity) — ein Soll gibt es
 * nicht, also auch kein „offen". Ebenso bei Positionen ohne Sollmenge.
 */
export interface BookingProgressState {
    used: number;
    remaining: number;
    /** Menge ueber dem Soll (nie negativ). */
    over: number;
}

export interface BookingProgress extends BookingProgressState {
    /** Soll vorhanden (nicht NEP, Menge > 0) — nur dann gibt es offen/ueber. */
    hasTarget: boolean;
    contracted: number;
    /** Fortschritt fuer den Balken, 0–100 — aus den UNgerundeten Mengen, nie NaN. */
    percent: number;
    /** Stand nach der eingegebenen Menge; null ohne gueltige Eingabe. */
    after: BookingProgressState | null;
}

// Mengen wie 0,1 + 0,2 sollen als 0,3 erscheinen, nicht als 0,30000000000000004.
const r2 = (n: number): number => Math.round(n * 100) / 100;

const progressState = (used: number, contracted: number): BookingProgressState => ({
    used: r2(used),
    remaining: r2(Math.max(0, contracted - used)),
    over: r2(Math.max(0, used - contracted)),
});

export const bookingProgress = (service: ServiceItem, amount: number): BookingProgress => {
    const contracted = contractedQuantity(service);
    const used = service.quantityUsed || 0;
    const hasTarget = !isNepService(service) && contracted > 0;
    const now = progressState(used, hasTarget ? contracted : used);
    const after = Number.isFinite(amount) && amount > 0
        ? progressState(used + amount, hasTarget ? contracted : used + amount)
        : null;
    const ratio = hasTarget ? (used / contracted) * 100 : 0;
    const percent = Number.isFinite(ratio) ? Math.min(100, Math.max(0, ratio)) : 0;
    return { ...now, hasTarget, contracted: r2(contracted), percent, after };
};

export const buildServiceDetail = (
    service: ServiceItem,
    serviceLog: ReadonlyArray<ServiceTransaction> | null | undefined,
    flatCovered = false,
): ServiceDetail => {
    const entries = (serviceLog || []).filter(l => l?.serviceId === service.id).map(toEntry);
    const work = entries.filter(e => e.kind === 'work').sort(neuesteZuerst);
    const postings = entries.filter(e => e.kind !== 'work').sort(neuesteZuerst);
    return {
        summary: summarizeService(service, flatCovered),
        work,
        workTotal: work.reduce((sum, e) => sum + e.quantity, 0),
        postings,
    };
};
